import { LensesCard } from './lenses-card';
import { LensesListProps } from './lenses-list.models';
import { useStyles } from './lenses-list.styles';
import { useDependentColors } from '../../../../../../../hooks/use-dependent-colors';
import {
  IProduct,
  ProductOwner,
  ProductOwnerType,
} from '../../../../../../../graphql/order/models/get-orders.models';
import { useMemo } from 'react';
import { ProfileData } from '../../../../profile-label';
import {
  TFormValue,
} from '../../../../order/order-create-page/order-create-steps/add-lenses-step/add-lenses-step-form';
import {
  getDependentProfile,
} from '../../../../order/order-create-page/order-create-steps/confirm-step/utils/get-dependent-profile';
import { SolutionsBrandType } from '../../../../../../../graphql/lenses/models/get-lenses.models';

export const LensesList = ({
  products, isEditMode, onUpdateQuantity,
}: LensesListProps): JSX.Element => {
  const classes = useStyles();
  const { getDependentColorIndex } = useDependentColors();

  const hasKidProfiles = useMemo(() => {
    return products.some((product) => {
      if ((product as TFormValue)?.dependent) {
        return true;
      } else {
        return (product as IProduct).productOwner?.type === ProductOwnerType.dependent;
      }
    });
  }, [products, ProductOwnerType]);

  return (
    <ul
      data-testid="lenses-list"
      className={classes.list}
    >
      {products.length ? products.map((product, index) => {
        const {
          eye,
          productQuantity,
          packSize,
          brand,
          id,
        } = product;
        const dependent = (product as TFormValue)?.dependent;

        const productOwner = (product as IProduct).productOwner
          ? (product as IProduct).productOwner
          : dependent ? ProductOwnerType.dependent : ProductOwnerType.consumer;
        const profile = (productOwner as ProductOwner).profile || (!isEditMode && productOwner)
          ? (productOwner as ProductOwner).profile
          : getDependentProfile(getDependentColorIndex, dependent);
        const isConsumerWithoutProfile = hasKidProfiles
          && (productOwner as ProductOwner).type === ProductOwnerType.consumer
          && !profile;

        const profileColorIndex =
          (productOwner as ProductOwner)?.type === ProductOwnerType.dependent &&
          profile?.firstName &&
          profile?.lastName
            ? getDependentColorIndex(profile.firstName, profile.lastName)
            : undefined;

        const profileProp: ProfileData | undefined = (() => {
          if (dependent) {
            return profile as ProfileData;
          }

          if (isConsumerWithoutProfile) {
            return {
              firstName: 'Consumer',
              lastName: '',
              isActive: true,
              colorIndex: profileColorIndex ?? 0,
              ownerType: ProductOwnerType.consumer,
            };
          }

          if (profile) {
            return {
              ...profile,
              ownerType: (productOwner as ProductOwner)?.type ?? ProductOwnerType.consumer,
              colorIndex: profileColorIndex ?? (profile as any).colorIndex,
            } as ProfileData;
          }

          return undefined;
        })();

        return (
          <li
            key={isEditMode ? id : index}
            data-testid="lenses-list-item"
            className={classes.listItem}
          >
            <LensesCard
              eye={eye}
              productQuantity={productQuantity}
              brand={brand!}
              packSize={packSize}
              isEditMode={isEditMode}
              onUpdateQuantity={onUpdateQuantity}
              id={id as string}
              profile={!hasKidProfiles || (!!productOwner && !profile) ? null : profileProp}
              isDeleted={(brand === SolutionsBrandType.arl || !hasKidProfiles) ? false : !profile}
            />
          </li>
        );
      }) : null}
    </ul>
  );
};
